import React from 'react';
import { Paper, Box, Typography, Skeleton } from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell
} from 'recharts';
import { formatCurrency } from '../../utils/dashboardHelpers';

/**
 * Gráfico de Top 5 Servicios más solicitados
 * Muestra barras horizontales con cantidad de servicios e ingresos
 */
const TopServiciosChart = ({ data, loading = false }) => {
  if (loading) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          background: 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
          border: '1px solid rgba(0, 0, 0, 0.08)',
          borderRadius: 2,
          height: 400
        }}
      >
        <Skeleton variant="text" width="60%" height={40} />
        <Skeleton variant="rectangular" width="100%" height={300} sx={{ mt: 2, borderRadius: 1 }} />
      </Paper>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 3,
          background: 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
          border: '1px solid rgba(0, 0, 0, 0.08)',
          borderRadius: 2,
          height: 400,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Typography color="text.secondary">No hay datos disponibles</Typography>
      </Paper>
    );
  }

  // Colores del gradiente para las barras
  const colors = ['#1A237E', '#283593', '#3949AB', '#5C6BC0', '#7986CB'];

  // Formatear datos para el gráfico
  const chartData = data.map(item => ({
    nombre: item.nombre_servicio?.length > 25
      ? item.nombre_servicio.substring(0, 25) + '...'
      : item.nombre_servicio,
    nombreCompleto: item.nombre_servicio,
    cantidad: parseInt(item.cantidad) || 0,
    ingresos: parseFloat(item.ingresos) || 0,
    porcentaje: parseFloat(item.porcentaje) || 0
  }));

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Paper
          elevation={3}
          sx={{
            p: 1.5,
            bgcolor: 'rgba(255, 255, 255, 0.98)',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            maxWidth: 250
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, fontSize: '0.875rem' }}>
            {data.nombreCompleto}
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#666' }}>
            Cantidad: <strong>{data.cantidad}</strong> ({data.porcentaje.toFixed(1)}%)
          </Typography>
          <Typography variant="body2" sx={{ fontSize: '0.8rem', color: '#666' }}>
            Ingresos: <strong>{formatCurrency(data.ingresos)}</strong>
          </Typography>
        </Paper>
      );
    }
    return null;
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        background: 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
        border: '1px solid rgba(0, 0, 0, 0.08)',
        borderRadius: 2,
        height: 400
      }}
    >
      {/* Header */}
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          color: '#1A237E',
          fontSize: '1.125rem',
          mb: 2
        }}
      >
        🏆 Top 5 Servicios Más Solicitados
      </Typography>

      {/* Gráfico */}
      <ResponsiveContainer width="100%" height={300}>
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" horizontal={false} />
          <XAxis
            type="number"
            stroke="#666"
            style={{ fontSize: '0.75rem' }}
          />
          <YAxis
            type="category"
            dataKey="nombre"
            stroke="#666"
            style={{ fontSize: '0.75rem' }}
            width={90}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(26, 35, 126, 0.05)' }} />
          <Bar
            dataKey="cantidad"
            radius={[0, 8, 8, 0]}
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Paper>
  );
};

export default TopServiciosChart;
